import type { GovernedSource } from "./api";
import type { SelectedContext } from "./storage";

/** The business audience a governed source serves, as classified in GenBI. */
export type DataDomain =
  | "service_operation"
  | "program_management"
  | "executive_reporting"
  | "corporate_finance";

/** Shown until a classified source is selected. */
export const DEFAULT_ANALYST_NAME = "AI Analytics";

const ANALYST_NAMES: Record<DataDomain, string> = {
  service_operation: "Service Operation Analyst",
  program_management: "Program Management Analyst",
  executive_reporting: "Executive Reporting Analyst",
  corporate_finance: "Corporate Finance Analyst",
};

const DOMAIN_LABELS: Record<DataDomain, string> = {
  service_operation: "Service Operation",
  program_management: "Program Management",
  executive_reporting: "Executive Reporting",
  corporate_finance: "Corporate Finance",
};

/** The business audience as a label, or null for an unclassified source. */
export function domainLabel(domain: unknown): string | null {
  return isDataDomain(domain) ? DOMAIN_LABELS[domain] : null;
}

export function isDataDomain(value: unknown): value is DataDomain {
  return typeof value === "string" && value in ANALYST_NAMES;
}

/**
 * The analyst name for a selection. Presentation only: the agent, its
 * instructions and its audit identity are the same whatever the domain.
 */
export function analystName(domain: unknown): string {
  return isDataDomain(domain) ? ANALYST_NAMES[domain] : DEFAULT_ANALYST_NAME;
}

/** The one way a source becomes a selection, so no page drops the domain. */
export function selectionFor(source: GovernedSource): SelectedContext {
  return {
    contextId: source.id,
    contextVersion: source.contextVersion,
    name: source.name,
    dataDomain: isDataDomain(source.dataDomain) ? source.dataDomain : null,
  };
}
